/**
 * FormPenilaian Component
 * Main form for patient satisfaction rating
 */

import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useKaryawan } from "../hooks/useKaryawan";
import { usePasien } from "../hooks/usePasien";
import { createPenilaian } from "../services/penilaianService";

import {
  INITIAL_FORM_DATA,
  JENIS_KELAMIN_OPTIONS,
  LAYANAN_OPTIONS,
  RATING_LABELS,
} from "../constants/formConstants";
import SearchPatient from "./SearchPatient";
import RatingGroup from "./RatingGroup";
import KaryawanPreview from "./KaryawanPreview";
import SuccessMessage from "./SuccessMessage";

const FormPenilaian = () => {
  const { karyawanNama } = useParams();
  const navigate = useNavigate();



  // Form state
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [submitted, setSubmitted] = useState(false);

  // Custom hooks for data fetching
  const {
    karyawanOptions,
    selectedKaryawanId,
    setSelectedKaryawanId,
    selectedKaryawan,
  } = useKaryawan(karyawanNama);

  const {  patientOptions,
    selectedPatientId,
    setSelectedPatientId } = usePasien();

  // Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePilihKaryawan = (e) => {
    const val = e.target.value;
    setSelectedKaryawanId(val);
    const k = karyawanOptions.find((x) => x.nik === val);
    if (k) {
      setFormData((prev) => ({ ...prev, karyawanNama: k.nik }));
      navigate(`/form/${encodeURIComponent(k.nik)}`);
    }
  };

  const handleSelectPatient = (patient) => {
    if (!patient) {
      setFormData((prev) => ({
        ...prev,
        nama: "",
        umur: "",
        jenisKelamin: "",
        tanggalKunjungan: "",
        layanan: "",
      }));
      return;
    }

    setSelectedPatientId(patient.id);

    setFormData((prev) => ({
      ...prev,
      nama: patient.nama,
      umur: patient.umur,
      jenisKelamin: patient.jenisKelamin,
      tanggalKunjungan: patient.tanggalKunjungan,
      layanan: patient.layanan
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const payload = {
      nik: selectedKaryawanId,
      no_rawat: selectedPatientId,
      dep_id: formData.layanan,
      keramahan_layanan: formData.keramahan,
      kejelasan_informasi: formData.kejelasanInformasi,
      kecapatan_layanan: formData.kecepatanLayanan,
      kritik_saran: formData.saran,
    };
  
    console.log("Payload dikirim:", payload);
  
    try {
      const data = await createPenilaian(payload);
      console.log("Response API:", data);
  
      if (data.success) {
        setSubmitted(true);
  
        setTimeout(() => {
          setSubmitted(false);
  
          setFormData({
            karyawanNama: "",
            nama: "",
            umur: "",
            jenisKelamin: "",
            tanggalKunjungan: "",
            layanan: "",
            kebersihan: "",
            keramahan: "",
            kejelasanInformasi: "",
            kecepatanLayanan: "",
            fasilitasRumahSakit: "",
            profesionalisme: "",
            saran: "",
          });
  
          setSelectedKaryawanId("");
          setSelectedPatientId("");
        }, 3000);
      } else {
        alert("❌ Gagal mengirim penilaian!");
      }
  
    } catch (error) {
      console.error("Error POST:", error.response?.data || error);
      alert("⚠️ Terjadi kesalahan pada server.");
    }
  };

  if (submitted) {
    return <SuccessMessage />;
  }

  return (
    <div className="App">
      <div className="form-container">
        <h1>Form Penilaian Kepuasan Pasien</h1>
        <h2>Rumah Sakit Aisyiyah Siti Fatimah</h2>
        <p className="subtitle">Pendapat Anda sangat penting bagi kami</p>

        {/* Form 1: Employee Selection */}
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="form-section">
            <h3>Data Karyawan yang Dinilai</h3>
            <div className="form-group">
              <label>Pilih Karyawan: *</label>
              <select
                name="selectedKaryawanId"
                value={selectedKaryawanId}
                onChange={handlePilihKaryawan}
                required
              >
                <option value="">-- Pilih Karyawan --</option>
                {karyawanOptions.map((k) => (
                  <option key={k.nik} value={k.nik}>
                    {k.nama} - {k.departemen}
                  </option>
                ))}
              </select>
            </div>

            {selectedKaryawan && (
              <KaryawanPreview karyawan={selectedKaryawan} />
            )}
          </div>
        </form>

        {/* Form 2: Patient Data & Rating */}
        <form onSubmit={handleSubmit}>
          {/* Patient Data Section */}
          <div className="form-section">
            <h3>Data Pasien</h3>

            <SearchPatient
              patientOptions={patientOptions}
              onSelectPatient={handleSelectPatient}
              required
            />

            <div className="form-group">
              <label>Umur: *</label>
              <input
                type="number"
                name="umur"
                value={formData.umur}
                onChange={handleChange}
                required
                placeholder="Masukkan umur"
                min="1"
                max="120"
              />
            </div>

            <div className="form-group">
              <label>Jenis Kelamin: *</label>
              <select
                name="jenisKelamin"
                value={formData.jenisKelamin}
                onChange={handleChange}
                required
              >
                <option value="">Pilih jenis kelamin</option>
                {JENIS_KELAMIN_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Tanggal Kunjungan: *</label>
              <input
                type="date"
                name="tanggalKunjungan"
                value={formData.tanggalKunjungan}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label>Jenis Layanan: *</label>
              <select
                name="layanan"
                value={formData.layanan}
                onChange={handleChange}
                required
              >
                <option value="">Pilih jenis layanan</option>
                {LAYANAN_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Rating Section */}
          <div className="form-section">
            <h3>Penilaian Layanan</h3>
            <p className="section-note">
              Berikan penilaian dari 1 (Sangat Tidak Ramah) sampai 5 (Sangat
              Ramah)
            </p>

            <RatingGroup
              label="Keramahan Staff"
              name="keramahan"
              value={formData.keramahan}
              onChange={handleChange}
              labels={RATING_LABELS.keramahan}
              maxRating={5}
              required
            />

            <RatingGroup
              label="Kejelasan Informasi"
              name="kejelasanInformasi"
              value={formData.kejelasanInformasi}
              onChange={handleChange}
              labels={RATING_LABELS.kejelasanInformasi}
              maxRating={4}
              required
            />

            <RatingGroup
              label="Kecepatan Layanan"
              name="kecepatanLayanan"
              value={formData.kecepatanLayanan}
              onChange={handleChange}
              labels={RATING_LABELS.kecepatanLayanan}
              maxRating={5}
              required
            />
          </div>

          {/* Feedback Section */}
          <div className="form-section">
            <h3>Saran & Kritik</h3>
            <div className="form-group">
              <label>Saran untuk Peningkatan Layanan:</label>
              <textarea
                name="saran"
                value={formData.saran}
                onChange={handleChange}
                rows="5"
                placeholder="Tuliskan saran atau kritik Anda di sini..."
              />
            </div>
          </div>

          <button type="submit" className="submit-btn">
            Kirim Penilaian
          </button>
        </form>
      </div>
    </div>
  );
};

export default FormPenilaian;
